import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Pencil, Undo2 } from 'lucide-react';
import { notifyPayslipSync } from '../../lib/payslipSync';

const TYPES = [
  ['resignation', 'Resignation'], ['termination', 'Termination'], ['retirement', 'Retirement'], ['contract_end', 'Contract End'],
  ['mutual_separation', 'Mutual Separation'], ['dismissal', 'Dismissal'], ['other', 'Other'],
];
const inputCls = 'dark:bg-slate-900 dark:border-slate-600';

// Edit termination details (incl. last working day) and revoke a termination. HR-only.
export function TerminationActions({ employee, onUpdated }) {
  const td = employee?.termination_details || {};
  const [editOpen, setEditOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [revokeReason, setRevokeReason] = useState('');

  const openEdit = () => {
    setForm({
      termination_date: td.termination_date || '', last_working_day: td.last_working_day || '',
      termination_type: td.termination_type || 'resignation', reason_for_leaving: td.reason_for_leaving || '',
      remarks: td.remarks || '', final_settlement_amount: td.final_settlement_manual_amount ?? '', change_reason: '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!form.last_working_day) return toast.error('Last working day is required');
    if ((form.change_reason || '').trim().length < 3) return toast.error('Please give a short reason for this change');
    setBusy(true);
    try {
      const payload = { ...form, final_settlement_amount: form.final_settlement_amount === '' ? 0 : Number(form.final_settlement_amount) };
      const res = await axios.put(`${API}/employees/${employee.employee_id}/termination`, payload);
      toast.success(res.data.employee_status === 'on_notice_period'
        ? 'Termination updated — employee is now on notice period until the new last working day'
        : 'Termination details updated');
      notifyPayslipSync(res.data?.payslip_sync);
      setEditOpen(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update termination');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (revokeReason.trim().length < 5) return toast.error('Please give a reason (at least 5 characters)');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/employees/${employee.employee_id}/terminate/revoke`, { reason: revokeReason.trim() });
      toast.success('Termination revoked — employee reinstated as Active');
      if (res.data?.payslip_sync?.status === 'updated') {
        toast.success(`${String(res.data.payslip_sync.month).padStart(2, '0')}/${res.data.payslip_sync.year} payslip restored to full-month pay`);
      }
      setRevokeOpen(false);
      setRevokeReason('');
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke termination');
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="flex items-center gap-2" data-testid="termination-actions">
      <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-termination-btn" className="h-8">
        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit Termination
      </Button>
      <Button size="sm" variant="outline" onClick={() => setRevokeOpen(true)} data-testid="revoke-termination-btn"
        className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-700">
        <Undo2 className="h-3.5 w-3.5 mr-1" /> Revoke Termination
      </Button>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg dark:bg-slate-800" data-testid="edit-termination-dialog">
          <DialogHeader>
            <DialogTitle>Edit Termination Details</DialogTitle>
            <DialogDescription>Changing the last working day re-prorates the final month, recomputes the final settlement and updates the employee status (on notice period vs terminated).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Resignation / Termination Date</Label>
              <Input type="date" value={form.termination_date || ''} onChange={set('termination_date')} data-testid="edit-termination-date" className={inputCls} />
            </div>
            <div>
              <Label>Last Working Day *</Label>
              <Input type="date" value={form.last_working_day || ''} onChange={set('last_working_day')} data-testid="edit-termination-lwd" className={inputCls} />
            </div>
            <div>
              <Label>Type</Label>
              <select value={form.termination_type || 'resignation'} onChange={set('termination_type')} data-testid="edit-termination-type"
                className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-900 dark:text-slate-100">
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label>Manual Final Settlement (RM)</Label>
              <Input type="number" min="0" step="0.01" placeholder="blank = auto" value={form.final_settlement_amount ?? ''} onChange={set('final_settlement_amount')} data-testid="edit-termination-fs" className={inputCls} />
            </div>
            <div className="col-span-2">
              <Label>Reason for Leaving</Label>
              <textarea value={form.reason_for_leaving || ''} onChange={set('reason_for_leaving')} rows={2} data-testid="edit-termination-reason"
                className="w-full rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100" />
            </div>
            <div className="col-span-2">
              <Label>Remarks</Label>
              <Input value={form.remarks || ''} onChange={set('remarks')} data-testid="edit-termination-remarks" className={inputCls} />
            </div>
            <div className="col-span-2">
              <Label>Why are you changing this? *</Label>
              <Input value={form.change_reason || ''} onChange={set('change_reason')} placeholder="e.g. Employee agreed to extend notice by 2 weeks" data-testid="edit-termination-change-reason" className={inputCls} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="edit-termination-save-btn">{busy ? 'Saving…' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800" data-testid="revoke-termination-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 dark:text-emerald-300">Revoke Termination</DialogTitle>
            <DialogDescription>
              {employee?.full_name} will be reinstated as <strong>Active</strong>. The termination record is archived in the employee history, the final settlement is cleared and any approved resignation request is marked revoked.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason *</Label>
            <textarea value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} rows={3} data-testid="revoke-termination-reason"
              placeholder="e.g. Employee withdrew resignation and will continue employment"
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={revoke} disabled={busy} data-testid="revoke-termination-confirm-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy ? 'Reinstating…' : 'Confirm Revoke'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
